/**
 * sl-oll9.15: StreamLainEventBus
 *
 * Type-safe event bus for all StreamLain custom events.
 * Replaces string-based window.dispatchEvent/addEventListener with
 * compile-time checked event names and payload types.
 *
 * Bridges to window CustomEvents for backward compatibility with
 * BlockSuite Lit components that can't inject services.
 */

import { Service } from '@toeverything/infra';

// =============================================================================
// Event Type Map — Single Source of Truth for all StreamLain events
// =============================================================================

export interface StreamLainEvents {
  // Automation events
  'automation:configure': { blockId: string };
  'automation:validation-error': { blockId: string; reasonKey: string };
  'automation:run': {
    blockId: string;
    docId: string;
    /** sl-1ahs.7.4: harmonised with backend ExecutionMode */
    mode: 'execute' | 'trigger';
  };
  'automation:run-complete': { blockId: string; status: 'success' | 'error' };
  'automation:trigger-sync': { blockId: string; triggerConfig: unknown };
  'automation:title-changed': void;

  // Fullscreen events
  'elster:fullscreen': { blockId: string };
  'database:fullscreen': { databaseId: string };
  'database:tab-switch': { databaseId: string };
  'database:tab-add': void;
  'database:tabs-reorder': { tabs: string[] };
  'database:tab-context-menu': {
    tabId: string;
    tabTitle: string;
    titleLoading?: boolean;
    x: number;
    y: number;
  };
  'database:tab-rename': { tabId: string };
  'database:fullscreen-context': {
    action: 'open' | 'close' | 'tab-switch';
    databaseId: string;
    databaseName: string;
    docId: string;
  };
  'database:fullscreen-row-selection': {
    databaseId: string;
    selectedRowIds: string[];
    selectedRowLabel: string;
  };

  // Navigation / UI events
  'ghost:open-trashcan': {
    blockId: string;
    ghostType: string;
    _routed?: boolean;
  };
  'pending-decision:review': {
    pendingDecisionId: string;
    _routed?: boolean;
  };
  'tutorial:card-start': { sessionId: string };

  // Inline editing: custom action events (sl-9qm.14.3)
  'inline-edit:automation-trigger': {
    automationBlockId: string;
    rowId: string;
    docId: string;
  };

  // BlockSuite integration events
  'attachment:pointer-drop': { blobId: string };
  'database:discovery-updated': void;
}

// =============================================================================
// Event Bus Service
// =============================================================================

type EventHandler<T> = T extends void ? () => void : (detail: T) => void;

export class StreamLainEventBus extends Service {
  private readonly handlers = new Map<string, Set<EventHandler<unknown>>>();

  /**
   * Subscribe to a typed event. Returns unsubscribe function.
   */
  on<K extends keyof StreamLainEvents>(
    event: K,
    handler: EventHandler<StreamLainEvents[K]>
  ): () => void {
    let set = this.handlers.get(event as string);
    if (!set) {
      set = new Set();
      this.handlers.set(event as string, set);
    }
    set.add(handler as EventHandler<unknown>);
    return () => {
      set!.delete(handler as EventHandler<unknown>);
      if (set!.size === 0) {
        this.handlers.delete(event as string);
      }
    };
  }

  /**
   * Emit a typed event to all subscribers.
   */
  emit<K extends keyof StreamLainEvents>(
    ...args: StreamLainEvents[K] extends void
      ? [event: K]
      : [event: K, detail: StreamLainEvents[K]]
  ): void {
    const [event, detail] = args;
    const set = this.handlers.get(event as string);
    if (!set) return;
    for (const handler of set) {
      (handler as (detail?: unknown) => void)(detail);
    }
  }

  /**
   * Bridge: Forward a window CustomEvent to the typed bus.
   * Use this for BlockSuite Lit components that dispatch window events.
   *
   * Returns cleanup function that removes the window listener.
   */
  bridgeFromWindow(
    windowEventName: string,
    busEventName: keyof StreamLainEvents
  ): () => void {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const set = this.handlers.get(busEventName as string);
      if (!set) return;
      for (const fn of set) {
        (fn as (d?: unknown) => void)(detail);
      }
    };
    window.addEventListener(windowEventName, handler);
    return () => window.removeEventListener(windowEventName, handler);
  }

  override dispose(): void {
    this.handlers.clear();
  }
}
